import React, { useEffect, useRef, useState, useCallback } from 'react';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import { sanitizeWorkflowHtmlForPersist } from '../../../shared/utils/sanitizeWorkflowHtml';
import { mediaLibraryUrlFor, mediaIdFromLibraryUrl } from '../../../shared/utils/mediaLibraryUrls';
import { resolveMaintellaMediaInHtml } from '../../../shared/utils/resolveWorkflowMediaHtml';
import AdminModal from '../common/AdminModal';
import useMediaLibrary from '../../hooks/useMediaLibrary';
import MediaLibraryPickerModal from '../media-library/MediaLibraryPickerModal';
import { registerWorkflowQuillImage } from '../../utils/workflowQuillImage';

registerWorkflowQuillImage();

function normalizeHtmlForSave(root) {
  if (!root) return '';
  const clone = root.cloneNode(true);
  clone.querySelectorAll('img').forEach((img) => {
    const mediaId =
      img.dataset.mediaLibraryId || mediaIdFromLibraryUrl(img.getAttribute('src'));
    if (mediaId) {
      img.setAttribute('src', mediaLibraryUrlFor(mediaId));
      delete img.dataset.mediaLibraryId;
    }
  });
  return clone.innerHTML;
}

function doSave(quillRef, onSave, onClose) {
  if (quillRef.current) {
    const html = normalizeHtmlForSave(quillRef.current.root);
    if (typeof onSave === 'function') {
      onSave(html);
    }
  }
  onClose();
}

async function resolveMaintellaMediaInEditor(root, fetchDownloadUrl) {
  if (!root) return;
  root.innerHTML = await resolveMaintellaMediaInHtml(root.innerHTML, fetchDownloadUrl, {
    trackMediaIds: true,
  });
}

export default function NodeContentEditorModal({ show, onClose, initialValue, onSave }) {
  const editorRef = useRef(null);
  const quillRef = useRef(null);
  const pasteHandlerRef = useRef(null);
  const dropHandlerRef = useRef(null);
  const fileInputRef = useRef(null);
  const editorInitializedRef = useRef(false);
  const [showLibraryPicker, setShowLibraryPicker] = useState(false);
  const [uploadNotice, setUploadNotice] = useState('');
  const { uploadFile, uploading, fetchDownloadUrl } = useMediaLibrary({ enabled: show });

  const insertMediaImage = useCallback((mediaId) => {
    if (!quillRef.current || !mediaId) return;
    const url = mediaLibraryUrlFor(mediaId);
    const range = quillRef.current.getSelection(true);
    quillRef.current.insertEmbed(range.index, 'image', url);
    quillRef.current.setSelection(range.index + 1);

    const images = quillRef.current.root.querySelectorAll('img');
    const inserted = images[images.length - 1];
    if (inserted) {
      inserted.dataset.mediaLibraryId = String(mediaId);
    }

    resolveMaintellaMediaInEditor(quillRef.current.root, fetchDownloadUrl);
  }, [fetchDownloadUrl]);

  const handleImageUpload = useCallback(async (file) => {
    if (!file) return;
    setUploadNotice('');
    try {
      const result = await uploadFile(file);
      if (result?.media?.id) {
        if (result.deduplicated) {
          setUploadNotice('Reused existing library image (duplicate detected).');
        }
        insertMediaImage(result.media.id);
      }
    } catch {
      // error surfaced via hook
    }
  }, [uploadFile, insertMediaImage]);

  const destroyQuill = useCallback(() => {
    if (quillRef.current) {
      const root = quillRef.current.root;
      if (pasteHandlerRef.current) {
        root.removeEventListener('paste', pasteHandlerRef.current);
        pasteHandlerRef.current = null;
      }
      if (dropHandlerRef.current) {
        root.removeEventListener('drop', dropHandlerRef.current);
        dropHandlerRef.current = null;
      }
      quillRef.current = null;
    }
    if (editorRef.current) {
      editorRef.current.innerHTML = '';
    }
    editorInitializedRef.current = false;
  }, []);

  useEffect(() => {
    if (!show) {
      destroyQuill();
      return undefined;
    }

    if (!editorRef.current || editorInitializedRef.current) {
      return undefined;
    }

    quillRef.current = new Quill(editorRef.current, {
        theme: 'snow',
        placeholder: 'Write content here...',
        modules: {
          toolbar: {
            container: [
              ['bold', 'italic', 'underline'],
              [{ list: 'ordered' }, { list: 'bullet' }],
              ['link', 'image'],
              ['clean'],
            ],
            handlers: {
              image: () => fileInputRef.current?.click(),
            },
          },
        },
      });

      const onPaste = (event) => {
        const items = event.clipboardData?.items;
        if (!items) return;

        const imageItem = Array.from(items).find((item) => item.type.startsWith('image/'));
        if (!imageItem) return;

        event.preventDefault();
        const file = imageItem.getAsFile();
        if (file) handleImageUpload(file);
      };

      const onDrop = (event) => {
        const file = event.dataTransfer?.files?.[0];
        if (!file || !file.type.startsWith('image/')) return;

        event.preventDefault();
        handleImageUpload(file);
      };

      pasteHandlerRef.current = onPaste;
      dropHandlerRef.current = onDrop;
      quillRef.current.root.addEventListener('paste', onPaste);
      quillRef.current.root.addEventListener('drop', onDrop);

      if (initialValue) {
        quillRef.current.root.innerHTML = sanitizeWorkflowHtmlForPersist(initialValue);
        resolveMaintellaMediaInEditor(quillRef.current.root, fetchDownloadUrl);
      }

    editorInitializedRef.current = true;

    return () => {
      destroyQuill();
    };
  }, [show, initialValue, fetchDownloadUrl, handleImageUpload, destroyQuill]);

  useEffect(() => {
    if (show && quillRef.current && initialValue) {
      quillRef.current.root.innerHTML = sanitizeWorkflowHtmlForPersist(initialValue);
      resolveMaintellaMediaInEditor(quillRef.current.root, fetchDownloadUrl);
    }
  }, [show, initialValue, fetchDownloadUrl]);

  const handleLibrarySelect = (item) => {
    insertMediaImage(item.id);
    setShowLibraryPicker(false);
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (file) handleImageUpload(file);
  };

  return (
    <>
      <AdminModal
        show={show}
        onClose={onClose}
        title="Edit Node Content"
        size="lg"
        footer={
          <>
            <button
              type="button"
              className="btn btn-outline-primary me-2"
              onClick={() => setShowLibraryPicker(true)}
            >
              <i className="bi bi-images me-1" />
              Library
            </button>
            <button className="btn btn-success" onClick={() => doSave(quillRef, onSave, onClose)}>
              Save
            </button>
            <button className="btn btn-outline-secondary ms-2" onClick={onClose}>
              Cancel
            </button>
          </>
        }
      >
        {(uploading || uploadNotice) && (
          <div className="small text-muted mb-2">
            {uploading ? 'Uploading image…' : uploadNotice}
          </div>
        )}
        <div style={{ minHeight: '275' }}>
          <div ref={editorRef} style={{ height: '100%' }} />
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="d-none"
          onChange={handleFileChange}
        />
      </AdminModal>

      <MediaLibraryPickerModal
        show={showLibraryPicker}
        onClose={() => setShowLibraryPicker(false)}
        onSelect={handleLibrarySelect}
      />
    </>
  );
}
